import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import AuthCallback from '@/components/AuthCallback';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import Dashboard from '@/pages/Dashboard';
import Drivers from '@/pages/Drivers';
import DriverProfile from '@/pages/DriverProfile';
import Documents from '@/pages/Documents';
import Fleet from '@/pages/Fleet';
import VehicleProfile from '@/pages/VehicleProfile';
import Inspections from '@/pages/Inspections';
import Violations from '@/pages/Violations';
import Accidents from '@/pages/Accidents';
import Training from '@/pages/Training';
import ServiceRequests from '@/pages/ServiceRequests';
import Notifications from '@/pages/Notifications';
import Todos from '@/pages/Todos';
import Users from '@/pages/Users';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import AuditReady from '@/pages/AuditReady';
import GuestAuditView from '@/pages/GuestAuditView';
import DriverInfo from '@/pages/DriverInfo';
import ResetPassword from '@/pages/ResetPassword';
import Landing from '@/pages/Landing';
import Plans from '@/pages/Plans';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Loader2, Clock } from 'lucide-react';

function ApprovalPending() {
  const { logout } = useAuth();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[hsl(var(--foreground))] p-4 text-center">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className="mb-5 flex justify-center"><Logo /></div>
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--gold))]/15"><Clock className="h-7 w-7 text-[hsl(var(--gold))]" /></div>
        <h1 className="font-display text-xl font-semibold">Awaiting Developer approval</h1>
        <p className="mt-2 text-sm text-muted-foreground">Your Company Admin account has been created and is pending approval by the system Developer. You'll gain full access once approved.</p>
        <Button variant="outline" className="mt-6" onClick={logout} data-testid="approval-logout-button">Sign out</Button>
      </div>
    </div>
  );
}

function Protected({ children, gate = true }) {
  const { user, loading, tenant, tenantLoading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === 'pending_approval') return <ApprovalPending />;
  if (gate) {
    if (tenantLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    const active = user.role === 'developer' || tenant?.subscription?.active;
    if (!active) return <Navigate to="/plans" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  const h = location.hash || '';
  if (h.includes('gtoken=') || h.includes('gpend=') || h.includes('gerror=') || h.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/audit-view/:token" element={<GuestAuditView />} />
      <Route path="/plans" element={<Protected gate={false}><Plans /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/drivers" element={<Protected><Drivers /></Protected>} />
      <Route path="/drivers/:id" element={<Protected><DriverProfile /></Protected>} />
      <Route path="/my-info" element={<Protected><DriverInfo /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/fleet" element={<Protected><Fleet /></Protected>} />
      <Route path="/fleet/:id" element={<Protected><VehicleProfile /></Protected>} />
      <Route path="/inspections" element={<Protected><Inspections /></Protected>} />
      <Route path="/violations" element={<Protected><Violations /></Protected>} />
      <Route path="/accidents" element={<Protected><Accidents /></Protected>} />
      <Route path="/training" element={<Protected><Training /></Protected>} />
      <Route path="/service-requests" element={<Protected><ServiceRequests /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/todos" element={<Protected><Todos /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/audit" element={<Protected><AuditReady /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Router />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
